/* Fælles design-primitiver for Votra Business 2.0 (navy/guld, editorial B2B).
   INGEN framer-motion: biblioteket skrev `opacity: 0` som inline style, og det lå i
   hovedchunken sammen med forsiden. Alt herunder er CSS og rene transforms. */
import React, { useRef } from "react";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const GOLD = "#D4AF37";
export const GOLD_DARK = "#8B7500";
export const NAVY = "#0B1220";
export const NAVY_SOFT = "#0F172A";

/* Crawler-sikker indtoning. framer-motion skrev `opacity: 0` som inline style, og fordi
   animationen var scroll-udløst, blev alt under heroen snapshottet skjult af prerenderingen.
   CSS-animationen har kun opacity i @keyframes, så indholdet er synligt, hvis animationen
   ikke kører. Brug ALDRIG framer-motion til indholdsblokke på offentlige sider. */
export const Reveal = ({ children, i = 0, className = "", as: As = "div", style, ...rest }) => (
  <As
    className={`votra-rise ${className}`}
    style={i ? { animationDelay: `${Math.min(i, 5) * 70}ms`, ...style } : style}
    {...rest}
  >
    {children}
  </As>
);

export const Aurora = ({ grid = false }) => (
  <div className="votra-aurora-wrap" aria-hidden>
    {grid && <div className="absolute inset-0 votra-gridlines opacity-60" />}
    <div className="votra-blob votra-blob--gold" />
    <div className="votra-blob votra-blob--cyan" />
    <div className="votra-blob votra-blob--gold2" />
  </div>
);

export const MagneticButton = ({ children, className = "", style, onClick, to, href, ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.25;
    const dy = (e.clientY - (r.top + r.height / 2)) * 0.4;
    el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = "translate3d(0, 0, 0)";
  };
  const shared = {
    ref,
    onMouseMove: onMove,
    onMouseLeave: reset,
    className: `votra-magnetic ${className}`,
    style,
    ...rest,
  };
  if (to) return <Link to={to} onClick={onClick} {...shared}>{children}</Link>;
  if (href) return <a href={href} onClick={onClick} {...shared}>{children}</a>;
  return <button type="button" onClick={onClick} {...shared}>{children}</button>;
};

export const Overline = ({ children, light }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.28em] mb-4"
     style={{ color: light ? GOLD : GOLD_DARK }}>
    {children}
  </p>
);

export const SectionHead = ({ overline, title, intro, light, className = "", align = "left" }) => (
  <Reveal className={`${align === "center" ? "mx-auto text-center max-w-3xl" : "max-w-3xl"} ${className}`}>
    {overline && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <Overline light={light}>{overline}</Overline>
      </div>
    )}
    {title && (
      <h2 className={`font-display text-3xl md:text-4xl lg:text-[2.75rem] font-medium leading-[1.12] ${light ? "text-white" : "text-slate-900"}`}>
        {title}
      </h2>
    )}
    {intro && (
      <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-slate-400" : "text-slate-600"}`}>{intro}</p>
    )}
  </Reveal>
);

export const SoonTag = ({ children = "Under udvikling" }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] border border-dashed"
        style={{ borderColor: "rgba(212,175,55,0.6)", color: GOLD_DARK, backgroundColor: "rgba(212,175,55,0.08)" }}>
    {children}
  </span>
);

export const Tag = ({ children, light }) => (
  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={light
          ? { backgroundColor: "rgba(212,175,55,0.16)", color: GOLD, border: `1px solid rgba(212,175,55,0.4)` }
          : { backgroundColor: "rgba(11,18,32,0.06)", color: NAVY, border: "1px solid rgba(11,18,32,0.12)" }}>
    {children}
  </span>
);

export const CheckItem = ({ children, light, testid }) => (
  <li className="flex items-start gap-3" data-testid={testid}>
    <Check className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: light ? GOLD : GOLD_DARK }} strokeWidth={2.5} />
    <span className={`text-sm md:text-[15px] leading-relaxed ${light ? "text-slate-300" : "text-slate-700"}`}>{children}</span>
  </li>
);

const GOLD_CLS = "group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-shadow hover:shadow-[0_0_40px_rgba(212,175,55,0.45)]";
const OUTLINE_CLS = "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border transition-colors";
const outlineTone = (light) =>
  light ? "text-white border-white/30 hover:bg-white/10" : "text-slate-800 border-slate-300 hover:bg-slate-100";

export const GoldButton = ({ children, onClick, href, className = "", testid, magnetic = true }) => {
  const cls = `${GOLD_CLS} ${className}`;
  const style = { backgroundColor: GOLD, color: NAVY };
  if (!magnetic) {
    const Tag = href ? "a" : "button";
    return (
      <Tag href={href} onClick={onClick} className={cls} style={style} data-testid={testid}>
        {children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Tag>
    );
  }
  return (
    <MagneticButton href={href} onClick={onClick} className={cls} style={style} data-testid={testid}>
      {children}<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
    </MagneticButton>
  );
};

/** Samme udtryk som GoldButton, men et rigtigt <a href> så søgemaskiner kan følge linket. */
export const GoldLink = ({ to, children, className = "", testid }) => (
  <MagneticButton to={to} className={`${GOLD_CLS} ${className}`} style={{ backgroundColor: GOLD, color: NAVY }} data-testid={testid}>
    {children}<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
  </MagneticButton>
);

export const OutlineButton = ({ children, onClick, light, className = "", testid }) => (
  <button onClick={onClick} data-testid={testid} className={`${OUTLINE_CLS} ${outlineTone(light)} ${className}`}>
    {children}
  </button>
);

export const OutlineLink = ({ to, children, light, className = "", testid }) => (
  <Link to={to} data-testid={testid} className={`${OUTLINE_CLS} ${outlineTone(light)} ${className}`}>
    {children}
  </Link>
);

/** Inline tekstlink i guld, bruges til "Læs mere"-links i blokke. */
export const TextLink = ({ to, children, className = "", testid }) => (
  <Link to={to} data-testid={testid}
    className={`inline-flex items-center gap-1.5 text-sm font-semibold hover:underline ${className}`}
    style={{ color: GOLD_DARK }}>
    {children}
  </Link>
);

/** Intern link-knap med outline-udtryk. */
export const LinkButton = ({ to, children, light, testid }) => (
  <OutlineLink to={to} light={light} testid={testid}>{children}</OutlineLink>
);

export const Card = ({ children, className = "", testid }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl p-6 md:p-7 transition-all duration-300 hover:shadow-xl hover:border-transparent votra-glow-edge ${className}`}
       data-testid={testid}>
    {children}
  </div>
);
